import { Radii, Spacing } from '~/types/ThemeTypes'
import {
  createMultiStyleConfigHelpers,
  MultiStyleType,
  ThemingProps,
} from '~/utils/chakraUtils'
import { mergeMultiStyleConfig } from '~/utils/styleHelpers'

import { textStyleKeys } from '@mangovoice/design-system'
import { textStyles } from '@mangovoice/design-system'

const dropzoneStyleParts = ['container', 'text', 'icon', 'clearButton'] as const
const helpers = createMultiStyleConfigHelpers(dropzoneStyleParts)

export enum DropzoneVariant {
  Default = 'default',
  Error = 'red.solid',
  Active = 'active',
  FileAccepted = 'fileAccepted',
  DragAccepted = 'dragAccepted',
}

export const DropzoneStyle = helpers.defineMultiStyleConfig({
  baseStyle: {
    container: {
      display: 'inline-flex',
      alignContent: 'center',
      justifyContent: 'center',
      borderRadius: Radii.md,
      backgroundColor: 'bg.panel',
      border: '1px dashed',
      borderColor: 'border.default',
      boxSizing: 'border-box',
      userSelect: 'none',
      outline: '2px solid transparent',
      outlineOffset: '2px',
      width: '100%',
      minHeight: '88px',
      textAlign: 'center',
      pl: Spacing._5,
      pr: Spacing._5,
      cursor: 'pointer',
      _hover: {
        borderColor: 'border.default',
      },
      _active: {
        borderColor: 'brand.solid',
        borderWidth: '2px',
      },
      _focus: {
        borderColor: 'brand.solid',
        borderWidth: '2px',
      },
    },
    text: {
      ...textStyles[textStyleKeys.Body2],
      textAlign: 'center',
      color: 'fg.muted',
    },
    icon: {
      color: 'fg.default',
      height: '2rem',
      width: '1.75rem',
    },
  },
  variants: {
    [DropzoneVariant.Error]: {
      container: {
        backgroundColor: 'red.subtle',
        borderColor: 'red.solid',
        _hover: {
          borderColor: 'red.solid',
        },
        _active: {
          borderColor: 'red.solid',
        },
      },
      text: {
        color: 'red.solid',
      },
    },
    [DropzoneVariant.Active]: {
      container: {
        borderColor: 'brand.solid',
        borderWidth: '2px',
        borderStyle: 'dashed',
        _hover: {
          borderColor: 'brand.solid',
        },
        _active: {
          borderColor: 'brand.solid',
        },
      },
    },
    [DropzoneVariant.DragAccepted]: {
      container: {
        backgroundColor: 'brand.subtle',
        borderColor: 'brand.solid',
        border: '2px dashed',
      },
      text: {
        color: 'brand.muted',
      },
      _hover: {
        borderColor: 'brand.solid',
      },
      _active: {
        borderColor: 'brand.solid',
      },
    },
    [DropzoneVariant.FileAccepted]: {
      container: {
        cursor: 'default',
        justifyContent: 'space-between',
        borderColor: 'fg.default',

        border: '1px solid',
        _hover: {
          borderColor: 'fg.default',
          border: '1px solid',
        },
        _active: {
          borderColor: 'fg.default',
          border: '1px solid',
        },
      },
      text: {
        color: 'fg.default',
      },
      icon: {
        color: 'fg.default',
      },
      clearButton: {
        color: 'fg.default',
        ...textStyles[textStyleKeys.Body2],
      },
    },
  },
})

export const useDropzoneStyles = (props?: ThemingProps) =>
  mergeMultiStyleConfig(DropzoneStyle, props) as MultiStyleType<
    typeof dropzoneStyleParts
  >
